/**
 * The stock covers shipped in src/assets/cover_images, and how a cover file
 * becomes the compressed data URL the create call uploads. Shared by the cover
 * picker (CoverImageComponent) and the create form's conversation, so a cover
 * picked either way is stored the same.
 */

export const STOCK_COVER_FILES: string[] = [
  'wedding1.jpg', 'wedding2.jpg', 'wedding3.jpg', 'wedding4.jpg',
  'birthday1.jpg', 'birthday2.jpg', 'birthday3.jpg',
  'corporate1.jpg', 'corporate2.jpg', 'corporate3.jpg',
  'holiday1.jpg', 'holiday2.jpg', 'holiday3.jpg', 'holiday4.jpg',
  'holiday5.jpg', 'holiday6.jpg', 'holiday7.jpg', 'holiday8.jpg',
];

export const stockCoverSrc = (file: string) => `assets/cover_images/${file}`;
export const stockCoverThumb = (file: string) => `assets/cover_images/thumbs/${file}`;

/** Three stock covers that suit an event category, best first. */
export function coversForCategory(category: string): string[] {
  const c = (category || '').toLowerCase();
  const group =
    /wedding|engagement|anniversary|shaadi|mehe?ndi|sangeet|haldi/.test(c) ? 'wedding'
      : /birthday|baby|party|graduation/.test(c) ? 'birthday'
        : /work|office|corporate|conference|offsite|meeting/.test(c) ? 'corporate'
          : 'holiday';
  return STOCK_COVER_FILES.filter(f => f.startsWith(group)).slice(0, 3);
}

/** Fetches a stock cover and compresses it the way the picker does. */
export async function loadStockCover(file: string): Promise<string> {
  const response = await fetch(stockCoverSrc(file));
  if (!response.ok) throw new Error(`Cover ${file} unavailable`);
  return compressImage(await response.blob(), 0.8);
}

const MAX_COVER_WIDTH = 800;

/** Scales to at most 800px wide and re-encodes as JPEG. */
export async function compressImage(file: Blob, quality: number): Promise<string> {
  const source = await decodeImage(file);
  const scale = Math.min(1, MAX_COVER_WIDTH / source.width);

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(source.width * scale));
  canvas.height = Math.max(1, Math.round(source.height * scale));

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  ctx.drawImage(source as CanvasImageSource, 0, 0, canvas.width, canvas.height);
  if ('close' in source) source.close();

  return canvas.toDataURL('image/jpeg', quality);
}

function decodeImage(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
    img.src = url;
  });
}
